import type {AugmentedFolderInterface} from 'PayloadUI/dataSelectors/buildPageMetaData';
import type {FolderInterface, Job, ListViewDataArticle, ListViewDataJobPosting, News} from 'types/payload-types';

/**
 * Type guard that checks whether a value is neither `undefined` nor `null`.
 * This utility is used to ensure that a given value is defined before performing operations on it.
 * It helps narrow down types in TypeScript, allowing the compiler to treat the value as fully defined (`T`).
 * This is especially useful in optional chaining, conditional rendering, or runtime validations.
 *
 * @param val The value to check.
 * @returns `true` if the value is not `undefined` and not `null`, otherwise `false`.
 *
 * @example
 * ```ts
 * if (isDefined(value)) {
 *   // value is guaranteed to be of type T
 *   use(value);
 * }
 * ```
 */
export const isDefined = <T>(val: T | null | undefined): val is T => val !== undefined && val !== null;

/**
 * Type guard that checks whether a value is a fully populated object rather than just a string reference.
 * This utility is typically used in Payload CMS where relationship fields can either return a string ID (unpopulated)
 * or a fully expanded object (populated). It helps distinguish between these cases at runtime, allowing safe access
 * to nested properties when the value is an object.
 *
 * @param val The value to check, which may be either a string or a populated object.
 * @returns `true` if the value is not a string (i.e. It's an object), otherwise `false`.
 *
 * @example
 * ```ts
 * if (isPopulated(relation.value)) {
 *   relation.value.slug; // safe to access
 * }
 * ```
 */
export const isPopulated = <T>(val: T | string): val is T => typeof val !== 'string';

/**
 * Type guard that checks whether a value is both defined and fully populated (not a string reference).
 * This utility combines the checks of `isDefined` and `isPopulated` to ensure that a value is neither `null` nor `undefined`,
 * and that it is a populated object rather than a string identifier. It is especially useful in CMS environments like Payload CMS,
 * where relationship fields may return either an ID (as a string) or a populated object depending on the query context.
 *
 * @param val The value to check, which may be `null`, `undefined`, a string ID, or a populated object.
 * @returns `true` if the value is defined and is not a string (i.e., it is a fully populated object).
 *
 * @example
 * ```ts
 * if (isDefinedAndPopulated(link.internalLink?.value)) {
 *   link.internalLink.value.slug; // Safe access
 * }
 * ```
 */
// eslint-disable-next-line max-len
export const isDefinedAndPopulated = <T>(val: T | string | null | undefined): val is T => isDefined(val) && isPopulated(val);

/**
 * Type guard that checks if a given object contains a `folder` property.
 * This utility helps ensure type safety when working with objects that may or may not have a `folder` key.
 * It is typically used to distinguish between general data objects and those that explicitly include folder-related information.
 * If the key `folder` exists on the object, the function returns `true`, allowing the object to be safely treated as
 * having a `FolderInterface`-typed `folder` property.
 *
 * @param val The value to check, usually a generic object or response payload.
 * @returns `true` if the object contains a `folder` key, otherwise `false`.
 *
 * @example
 * ```ts
 * if (hasFolder(data)) {
 *   data.folder.name; // safe access
 * }
 * ```
 */
export const hasFolder = (val: any): val is {folder: FolderInterface} => 'folder' in val;

/**
 * Type guard that checks if a given object contains a `documentsAndFolders` property.
 * This is useful when narrowing types in data structures that may include a populated list of documents and subfolders,
 * such as a deeply queried folder tree in a CMS. It ensures that logic working with document collections is only
 * applied to relevant objects.
 * If the `documentsAndFolders` key exists, the function returns `true`, allowing type-safe access to its value.
 *
 * @param val The value to check, typically a folder or nested object.
 * @returns `true` if the object contains a `documentsAndFolders` key, otherwise `false`.
 *
 * @example
 * ```ts
 * if (hasDocuments(folder)) {
 *   folder.documentsAndFolders.docs.forEach(doc => { ... });
 * }
 * ```
 */
export const hasDocuments = (val: any): val is AugmentedFolderInterface => 'documentsAndFolders' in val;

/**
 * Type guard that checks whether a given object includes a `listName` property.
 * This utility is useful when working with dynamic objects or content structures where certain fields
 * may or may not be present. Specifically, it ensures that the `listName` property exists so that it can
 * be safely accessed without risking runtime errors or type violations.
 * This is commonly used in list rendering, filtering, or UI labeling logic where a `listName` is expected
 * to be available for display or sorting purposes.
 *
 * @param val The value to check, typically an object from a CMS query or user-defined source.
 * @returns `true` if the object contains a `listName` key, otherwise `false`.
 *
 * @example
 * ```ts
 * if (hasListName(item)) {
 *   item.listName; // safe to use
 * }
 * ```
 */
export const hasListName = (val: any): val is {listName: string} => 'listName' in val;

/**
 * Type guard that determines whether a given object contains a `listView` property.
 * This utility is used to safely identify whether an object includes the `listView` field,
 * which typically holds metadata used for rendering summaries in overviews, such as blog articles or job postings.
 * It ensures type narrowing when working with heterogeneous content structures (e.g., union types or mixed folders).
 * If the `listView` key exists on the object, the function returns `true`, allowing access to the
 * corresponding `listView` data with type safety.
 *
 * @param val The value to inspect, usually a content item retrieved from Payload CMS.
 * @returns `true` if the object contains a `listView` property, otherwise `false`.
 *
 * @example
 * ```ts
 * if (hasListView(item)) {
 *   renderSummaryCard(item.listView);
 * }
 * ```
 */
export const hasListView = (val: any): val is {listView: Job['listView'] | News['listView']} => 'listView' in val;

/**
 * Type guard function that checks if an object contains a 'type' property.
 * This utility function is used to determine whether an object has a type property before
 * performing more specific type checking operations. It serves as a foundational guard
 * for other type checking functions that need to verify the existence of a type field.
 *
 * @param obj The object to check for the presence of a 'type' property.
 * @returns True if the object contains a 'type' property of string type, false otherwise.
 *
 * @example
 * ```tsx
 * const data = { type: 'Article', title: 'Sample' };
 * if (hasType(data)) {
 *     console.log(data.type); // TypeScript knows data has type property
 * }
 * ```
 */
const hasType = (obj: any): obj is {type: string} => obj !== null && obj !== undefined && 'type' in obj;

/**
 * Type guard function that determines if an object represents article data.
 * This function checks if the provided object has a 'type' property set to 'Article' and
 * narrows the type to `ListViewDataArticle`. It is commonly used to safely access
 * article-specific properties when processing mixed content types in lists or structured data.
 *
 * @param obj The object to check for article type classification.
 * @returns True if the object is of type 'Article' and can be treated as ListViewDataArticle, false otherwise.
 *
 * @example
 * ```tsx
 * const data = { type: 'Article', title: 'Sample Article' };
 * if (isArticleType(data)) {
 *     console.log(data.title); // TypeScript knows this is ListViewDataArticle
 * }
 * ```
 */
export const isArticleType = (obj: any): obj is ListViewDataArticle => hasType(obj) && obj.type === 'Article';

/**
 * Type guard function that determines if an object represents job posting data.
 * This function validates that the provided object has a 'type' property set to 'JobPosting'
 * and narrows the type to `ListViewDataJobPosting`. It enables safe access to job-specific
 * properties when handling heterogeneous content collections or generating structured data.
 *
 * @param obj The object to check for job posting type classification.
 * @returns True if the object is of type 'JobPosting' and can be treated as ListViewDataJobPosting, false otherwise.
 *
 * @example
 * ```tsx
 * const jobData = { type: 'JobPosting', title: 'Software Engineer' };
 * if (isJobPostingType(jobData)) {
 *     console.log(jobData.title); // TypeScript knows this is ListViewDataJobPosting
 * }
 * ```
 */
export const isJobPostingType = (obj: any): obj is ListViewDataJobPosting => hasType(obj) && obj.type === 'JobPosting';

/**
 * Type guard function that checks if an object contains a 'linkId' property.
 * This utility function validates whether a given object includes a `linkId` field, which is typically
 * used to identify link references or connection identifiers in content management systems or data structures.
 * It performs a null check and property existence validation to ensure type safety when accessing link-related data.
 *
 * @param obj The object to check for the presence of a 'linkId' property.
 * @returns True if the object is not null and contains a 'linkId' property of string type, false otherwise.
 *
 * @example
 * ```tsx
 * const linkData = { linkId: 'abc123', url: 'https://example.com' };
 * if (hasLinkId(linkData)) {
 *     console.log(linkData.linkId); // TypeScript knows linkData has linkId property
 * }
 * ```
 */
export const hasLinkId = (obj: any): obj is {linkId: string} => obj !== null && obj !== undefined && 'linkId' in obj;